import { CREATABLE_FILE_TYPES, createdFileName, isEditableFileName } from './file-edit';

// Creating a file is the editor with nothing in it yet.
//
// It used to be a small dialog that collected a name, a type and a folder, wrote an EMPTY file, and then
// opened the editor on it — two modals for one act, and the second one arrived on a blank document the
// first had just created. The `+` now opens the editor itself, holding a DRAFT: a name, a description, a
// destination and a body, none of which exist server-side until Save.
//
// Import-free of React and the data layer on purpose, like `doc-editable.ts` beside it, so the rules can be
// executed in a test rather than asserted against the source.

/** `WriteFileRequest.hint`'s own `(.wix.api.maxLength)`, mirrored so the field can cap itself and the
 *  routes' `hint_too_long` stays a server-side guard rather than a message the user has to read. Kept
 *  here rather than imported from the route: this module is import-free, and the native ports mirror it. */
export const MAX_HINT_CHARS = 1000;

/** The `.md` a name with no usable extension gets. The first creatable type, so the picker's old default
 *  and this agree by construction rather than by two lists happening to match. */
export const DEFAULT_DRAFT_EXTENSION = CREATABLE_FILE_TYPES[0].extension;

export interface FileDraft {
  /** Exactly what the user typed in the name field — not yet a filename. */
  name: string;
  /** The folder it will be written into: `''` means none chosen yet. */
  folder: string;
  /** The `hint` the agent reads to decide when this file is worth opening. Optional. */
  description: string;
  body: string;
}

/**
 * The filename a draft will actually be written as.
 *
 * The type PICKER is gone: the name carries its own extension, because that is how the file is named in
 * every other place it appears and a segmented control for three values is a lot of chrome for a suffix.
 * A name that already ends in an editable extension is taken as typed; anything else gets `.md`.
 *
 * `createdFileName` is the shared helper the old picker used, so "notes" → "notes.md" the same way it
 * always did, and a name the user gave a real extension is not given a second one.
 */
export function draftFileName(typed: string): string {
  const trimmed = typed.trim();
  if (!trimmed) return '';
  if (isEditableFileName(trimmed)) return trimmed;
  return createdFileName(trimmed, CREATABLE_FILE_TYPES[0].type);
}

/**
 * The name an UPLOADED file is filed under, given what the user typed and the file's own name.
 *
 * Deliberately NOT `draftFileName`, which is the AUTHORING rule: that one appends `.md` to any name whose
 * extension is not a text-shaped one, so putting an upload through it files `invoice.pdf` as
 * `invoice.pdf.md` — on every non-text upload there is. This rule instead trusts the extension the file
 * already has: a typed name carrying one is taken as typed, and one without inherits the picked file's,
 * so clearing the name to "invoice" still writes `invoice.pdf` rather than an extensionless file.
 *
 * Shared 1:1 with iOS `FileUpload.draftFileName(typed:original:)` and Android `uploadDraftFileName`.
 */
export function uploadFileName(typed: string, originalName: string): string {
  const trimmed = typed.trim();
  if (!trimmed) return '';
  if (fileExtensionOf(trimmed)) return trimmed;
  const extension = fileExtensionOf(originalName);
  return extension ? `${trimmed}.${extension}` : trimmed;
}

/** The extension after the last dot, or `''`. A leading dot is a hidden file, not an extension. */
function fileExtensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1) : '';
}

/** The full key a draft writes to, or `''` when it does not have one yet. */
export function draftPath(draft: Pick<FileDraft, 'name' | 'folder'>): string {
  const fileName = draftFileName(draft.name);
  // A file must live IN a folder: the top of the tree is a list of Spaces, not a place, and `planFileWrite`
  // refuses a key with no container. Same rule the move dialog applies.
  return draft.folder && fileName ? `${draft.folder}/${fileName}` : '';
}

export type DraftBlocker = 'name' | 'folder';

/**
 * Why Save is not available yet, or `null` when it is.
 *
 * Deliberately NOT a reason about the body: an empty file is a real file — that is the whole point of the
 * change that made the editor open one — so a draft with a name and a destination is saveable with nothing
 * typed in it. The description is optional too.
 *
 * `name` is reported ahead of `folder` because the name field is the one under the cursor.
 */
export function draftBlocker(draft: Pick<FileDraft, 'name' | 'folder'>): DraftBlocker | null {
  if (!draftFileName(draft.name)) return 'name';
  if (!draft.folder) return 'folder';
  return null;
}
